import { bucketHTML, kernelHTML } from './popcornArt.js'
// ── Stale-chunk recovery ──────────────────────────────────────────────────────
// After a deploy, an installed PWA can hold OLD HTML whose lazy imports point at
// hashed chunk files that no longer exist. The server (SPA fallback) returns
// index.html for the missing .js, so the browser throws:
//   "'text/html' is not a valid JavaScript MIME type"  (or "Failed to fetch
//    dynamically imported module").
// A plain reload can re-serve the SAME stale shell from the service-worker cache and
// loop, so recovery CLEARS the caches, UNREGISTERS the SW, and cache-busts the URL —
// guaranteeing a fresh network fetch. Guarded (shared 12s window with main.jsx's
// reload guard, same sessionStorage key) so it can never loop. (Jason 2026-07-21)

const RELOAD_AT = 'nextep_reload_at'
const RELOAD_COOLDOWN = 12000

// An INSTALLED standalone PWA (iOS home-screen especially) must never be
// navigated in place by JS — location.replace()/reload() corrupts the layout
// viewport there: position:fixed elements (the bottom nav) drift with a
// desynced touch target and taps stop landing until a force-quit (Jason
// 2026-07-22/23). main.jsx suppresses its auto-reload paths on this flag;
// exported so EVERY reload path shares one definition — the 2026-08-22
// dead-nav hunt found two paths (hardReloadForUpdate below, WhatsNew's
// "Update now") that had missed the policy.
export const isStandalonePWA = (() => {
  try { return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true }
  catch { return false }
})()

const claimed = () => { try { return Date.now() - (parseInt(sessionStorage.getItem(RELOAD_AT), 10) || 0) < RELOAD_COOLDOWN } catch { return false } }
const claim   = () => { try { sessionStorage.setItem(RELOAD_AT, String(Date.now())) } catch {} }

// The last two alternatives catch React.lazy's DOWNSTREAM symptom: when a lazy
// chunk fails to load (stale hash 404'd to index.html after a deploy) the initial
// fetch error can go uncaught and React re-renders the broken lazy payload, throwing
// a TypeError off its internal `_result` field — on iOS/WebKit:
//   "undefined is not an object (evaluating 'e._result.default')"
// and on Chrome: "Cannot read properties of undefined (reading 'default')". Both are
// definitionally a failed lazy import → recover instead of showing the dead card.
const STALE_RE = /valid JavaScript MIME type|MIME type of "?text\/html|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|module script failed|Unable to preload CSS|_result\.default|reading 'default'\)/i

export function isStaleChunkError(err) {
  if (!err) return false
  if (err.name === 'ChunkLoadError') return true
  const msg = err.message || (typeof err === 'string' ? err : (err.toString ? err.toString() : '')) || ''
  return STALE_RE.test(msg)
}

// Cover the reload transition with a branded splash so the user doesn't see the OLD
// app layout flash while the service worker hands over and the new bundle boots
// (Jason 2026-07-22). Injected straight into the live DOM so it paints synchronously,
// no React needed. Safe to call more than once (id-guarded).
const UPDATING_FLAG = 'nextep_updating'

export function showUpdatingSplash() {
  // Persist a flag so the splash survives the reload: the NEXT boot (which may briefly be
  // the OLD bundle served once more before the new SW takes over) re-shows it immediately,
  // covering the post-reload old-layout flash until the new app mounts (Jason 2026-07-22).
  try { sessionStorage.setItem(UPDATING_FLAG, '1') } catch { /* ignore */ }
  if (typeof document === 'undefined' || !document.body) return
  if (document.getElementById('nextep-updating-splash')) return
  const el = document.createElement('div')
  el.id = 'nextep-updating-splash'
  el.setAttribute('style', 'position:fixed;inset:0;z-index:2147483647;background:#07070f;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:15px;font-family:system-ui,-apple-system,sans-serif')
  // A popcorn bucket, not a generic spin ring — a plain border-spinner said
  // nothing about what app this was; this reads as NextEp on sight, even for
  // the ~1s most people see it (Jason 2026-08-02: "let's swap out our
  // 'updating' ring for something more tv'ish... a popcorn bucket animation
  // with popcorn overflowing"). Pure CSS/div shapes only, no image asset —
  // this fires before the bundle is trustworthy, so it can't depend on one.
  // Redesigned 2026-08-06 from a Claude Design export ("Popcorn Updating Icon"):
  // a bigger, clearer white/red-striped bucket with a scalloped rim, a full
  // overflowing mound, kernels scattered on the floor beneath it, and 7 kernels
  // arcing up and out on staggered loops. The bucket also sways gently.
  // The bucket itself comes from lib/popcornArt.js (same drawing as the in-app
  // mark); the splash adds a floor line, a contact shadow and a few spilled kernels.
  const spilled = [[18, 0, 9, -12, true], [34, 6, 8, 20, false], [150, 4, 9, -8, true], [170, 0, 8, 14, false], [62, 9, 7, 30, false], [128, 10, 7, -22, true]]
    .map(([x, b, w, rot, butter]) => kernelHTML({ x, y: 0, w, s: 1, rot, butter, extraStyle: `top:auto;bottom:${22 + b}px;opacity:0.85;` })).join('')
  el.innerHTML = `
    <div style="position:relative;width:220px;height:230px;">
      <div style="position:absolute;left:14px;right:14px;bottom:34px;height:2px;background:rgba(255,255,255,0.06);border-radius:2px;"></div>
      <div style="position:absolute;left:50%;bottom:30px;transform:translateX(-50%);width:150px;height:20px;background:radial-gradient(ellipse at center,rgba(0,0,0,0.5) 0%,rgba(0,0,0,0) 72%);"></div>
      ${spilled}
      <div style="position:absolute;left:50%;bottom:34px;transform:translateX(-50%);">${bucketHTML({ size: 128, animate: true })}</div>
    </div>
    <div id="nextep-updating-caption" style="color:#f5c842;font-size:14px;font-weight:600;letter-spacing:0.02em;max-width:260px;text-align:center;line-height:1.5">Popping up an update…</div>
    <style>
      @keyframes nxKernelArc {
        0%   { opacity:0; transform:translate(0,0) scale(0.35) rotate(0deg); }
        8%   { opacity:1; }
        48%  { transform:translate(var(--dx1),var(--dy1)) scale(1.05) rotate(var(--rot1)); }
        78%  { opacity:1; transform:translate(var(--dx2),var(--dy2)) scale(0.95) rotate(var(--rot2)); }
        92%  { opacity:1; }
        100% { opacity:0; transform:translate(var(--dx2),var(--dy2)) scale(0.85) rotate(var(--rot2)); }
      }
      @keyframes nxBucketSway {
        0%, 100% { transform:translateX(-50%) rotate(-1deg); }
        50%      { transform:translateX(-50%) rotate(1deg); }
      }
    </style>
  `
  document.body.appendChild(el)
}

// Standalone-only: show the popcorn splash as a brief TAP ACKNOWLEDGMENT, not a
// blocking wait. Jason 2026-08-22 liked the splash and wanted the SAME "Update
// available → tap → reload" experience on every platform, no separate silent
// path — but the earlier fix (2026-08-22) deliberately skipped the splash on
// standalone because sitting on it for however long it takes to background the
// app is worse than what it replaced. This resolves both: play the loved
// animation for a fixed ~1.3s beat (the actual reload is still deferred to
// backgrounding via deferReloadToBackground, untouched), then clear it and let
// the "Update queued" toast take over as the ongoing status.
const TAP_ACK_MS = 1300
export function showUpdatingSplashBriefly(ms = TAP_ACK_MS) {
  showUpdatingSplash()
  const cap = typeof document !== 'undefined' && document.getElementById('nextep-updating-caption')
  if (cap) cap.textContent = 'Fresh popcorn on the way…'
  setTimeout(clearUpdatingSplash, ms)
}

// True when a reload was initiated by an update path — main.jsx re-shows the splash on boot.
export function isUpdatingReload() {
  try { return sessionStorage.getItem(UPDATING_FLAG) === '1' } catch { return false }
}

// Fade out + remove the splash and clear the flag. Called once the fresh app has mounted.
export function clearUpdatingSplash() {
  try { sessionStorage.removeItem(UPDATING_FLAG) } catch { /* ignore */ }
  const el = typeof document !== 'undefined' && document.getElementById('nextep-updating-splash')
  if (!el) return
  el.style.transition = 'opacity 0.25s ease'
  el.style.opacity = '0'
  setTimeout(() => el.remove(), 260)
}

// Standalone can't be navigated in place (see isStandalonePWA above). The fallback
// used to just swap the full-screen splash's caption — but that splash is only ever
// on screen for ~1.3s (showUpdatingSplashBriefly's tap-ack), and this fires up to
// DEFER_FALLBACK_MS later, so the caption it "updated" was long gone: a silent
// no-op (Jason 2026-09-09 — tapped "Update," the splash flashed and vanished, then
// nothing, and no visible instruction ever told him what to do; he had to guess and
// force-quit it himself, exactly what this whole deferred-reload path exists to
// avoid). Dispatch a real, persistent instruction instead — the same popcorn pill
// renders it (WhatsNew.jsx listens for this).
export function showReopenMessage() {
  try { window.dispatchEvent(new CustomEvent('nextep:update-stuck')) } catch {}
}

// Was 20 minutes — far too long to leave someone with a silently-armed update and
// zero feedback if the reload-while-hidden experiment doesn't fire for them (the
// same 2026-09-09 report: nothing visible happened, so a real fallback message
// needs to land well before "stuck for 20 minutes" territory). 90s comfortably
// covers a normal tap-then-glance-away without nagging someone still reading.
const DEFER_FALLBACK_MS = 90 * 1000

// Do the actual navigate-in-place reload the standalone case has always
// avoided while VISIBLE — the touch-target desync is specifically a reload of
// the ACTIVE document, so firing this only once the page is hidden should
// sidestep it entirely: the user's ordinary next app-switch/lock becomes the
// "reopen" that force-quit used to require, with nothing for them to do.
// ROOT CAUSE of the standalone "app reboots on every page" storms (Jason 2026-09-13,
// and almost certainly Wednesday's loop): the standalone update path sent
// SKIP_WAITING the moment a new build was detected, while the page was VISIBLE. The
// new SW activated, clientsClaim took over the RUNNING old page, and from then on
// every lazy route asked the new precache for chunk hashes it doesn't have → 404 →
// SPA fallback HTML → "not a valid JavaScript MIME type" → stale-chunk recovery →
// unregister + clear caches → a page that can't load anything until force-quit.
// vite.config.js's own comment (skipWaiting:false) describes this exact footgun.
// The fix: the handoff happens HERE, off-screen, immediately before the navigation
// that lands on the new worker — never while the old page still needs its chunks.
function reloadNow() {
  const go = () => {
    try {
      const u = new URL(window.location.href)
      u.searchParams.set('_v', Date.now().toString(36))
      window.location.replace(u.toString())
    } catch { window.location.replace(window.location.href) }
  }
  try {
    navigator.serviceWorker?.getRegistration().then(reg => {
      const pending = reg?.waiting
      if (!pending) { go(); return }
      let done = false
      const once = () => { if (done) return; done = true; go() }
      try { navigator.serviceWorker.addEventListener('controllerchange', once, { once: true }) } catch {}
      pending.postMessage({ type: 'SKIP_WAITING' })
      setTimeout(once, 1500)   // wedged worker: navigate anyway, the old SW still serves the old shell
    }, go)
  } catch { go() }
}

// Standalone-only: an update is ready, caches/SW are already cleared by the
// caller. Never reload a VISIBLE standalone app (that's the desync bug) — wait
// for the page to go hidden and reload silently off-screen instead, so no
// force-quit is ever asked for in the common case. A bounded fallback still
// shows the old reopen prompt if they genuinely never background the app
// (Jason 2026-08-22: "find a way to prevent telling them to close and
// reopen it, however that may work"). This is a real experiment — the
// desync is a WebKit bug we don't control, and reload-while-hidden hasn't
// been verified on a real device, only reasoned about; watch for reports.
// Multiple update-detection sources (SW nextep:update-waiting, WhatsNew's 5-min
// poll, main.jsx's foreground poll) can each independently decide "time to
// reload" within the same up-to-20-minute deferred window — the 12s
// nextep_reload_at cooldown only suppresses BURSTS, it's long expired by the
// time a second detection fires. Without this flag each detection registered
// its own visibilitychange listener + timer, so the eventual backgrounding
// fired reloadNow() twice back-to-back. One pending defer per tab, period.
// UniversalImport.jsx sets this while a CSV/zip import is actively writing rows
// (it already existed to silence achievement toasts mid-import — reused here).
// A reload firing mid-batch wouldn't corrupt anything (each row commits as it
// goes), but it WOULD silently truncate the import with zero warning to the
// user. Cheap to check, so check it.
const isImportBusy = () => typeof window !== 'undefined' && window.__nextepImporting

// ── "Update queued" toast: once per pending VERSION, across page loads ─────────
// Two independent detectors notice a pending update — main.jsx's version.json
// check and WhatsNew's own poll — and each page load resets any in-memory guard,
// so a reload before the deferred swap fired the same toast again ("one and then
// a second on a new page load", Jason 2026-09-05). Key the dedupe on the TARGET
// version in localStorage: the toast fires once per version no matter which
// detector wins or how many loads happen before the update actually applies.
// main.jsx clears the key once the app boots ON that version, so the next real
// update announces again. `force` is for the explicit "Update now" tap — a user
// action always deserves feedback.
const UPDATE_TOAST_KEY = 'nextep_update_toast_ver'
export function announceQueuedUpdate(version, { force = false } = {}) {
  try {
    if (!force && version && localStorage.getItem(UPDATE_TOAST_KEY) === String(version)) return false
    if (version) localStorage.setItem(UPDATE_TOAST_KEY, String(version))
  } catch { /* storage blocked — fall through and toast */ }
  // Rendered by WhatsNew's popcorn pill (2026-09-08) — one update surface, not a toast
  // AND a sheet. The pill stays until the deferred reload actually happens.
  try { window.dispatchEvent(new CustomEvent('nextep:update-queued', { detail: { version: version || null } })) } catch {}
  return true
}
export function clearQueuedUpdateAnnouncement() {
  try { localStorage.removeItem(UPDATE_TOAST_KEY) } catch {}
}

// BUG FOUND 2026-09-14, live on Jason's account: `deferPending` was set true on
// arm and NEVER reset. If the 90s fallback fires because the app was never
// backgrounded in time, every LATER attempt (the next foreground check, the next
// cold boot within the same session) silently no-ops on `if (deferPending) return`
// — permanently, for the rest of that session. Combined with main.jsx's circuit
// breaker (which also gives up after one try), the net effect was: one missed
// backgrounding and the update can NEVER apply again without a real page reload,
// with no further nudge once the "Almost there" pill was missed or dismissed.
// `finish()` is the single completion point for both outcomes (success OR
// fallback) — reset there so a fallback can be followed by a genuine retry.
let deferPending = false
// `toast`, when passed, announces the queued update — but ONLY on the call that
// actually arms the defer. Callers like main.jsx's version-mismatch check re-run
// on every foreground (cold boot, then again on the next visibilitychange) and
// will keep re-detecting the SAME still-pending mismatch until it actually
// reloads; dispatching the toast at the call site rather than here fired it once
// per redetection — the same "🍿 Update queued" toast stacking twice for one
// real update (Jason 2026-09-05, screenshot of two identical toasts). Gating on
// the `deferPending` flip below makes it structurally impossible to double-fire:
// a second call for the same pending update no-ops before it ever reaches this.
export function deferReloadToBackground({ onFallback, toast } = {}) {
  if (typeof document === 'undefined') { reloadNow(); return }
  if (document.hidden && !isImportBusy()) { reloadNow(); return }
  if (deferPending) return
  deferPending = true
  if (toast) { try { window.dispatchEvent(new CustomEvent('nextep:toast', { detail: toast })) } catch {} }
  let done = false
  const finish = (fn) => {
    if (done) return
    done = true
    document.removeEventListener('visibilitychange', onHidden)
    clearInterval(importPoll)
    clearTimeout(fallbackTimer)
    deferPending = false   // release the guard — see the note above `let deferPending`
    fn()
  }
  const tryReload = () => {
    if (document.visibilityState === 'hidden' && !isImportBusy()) finish(reloadNow)
  }
  const onHidden = () => tryReload()
  document.addEventListener('visibilitychange', onHidden)
  // While hidden, an in-progress import can hold this open past the one
  // visibilitychange event that got us here — poll so the reload fires the
  // moment the import finishes instead of waiting for another background/
  // foreground cycle that may never come.
  const importPoll = setInterval(tryReload, 2000)
  const fallbackTimer = setTimeout(() => finish(onFallback || showReopenMessage), DEFER_FALLBACK_MS)
}

// Force a clean, fresh-from-network reload. Use directly for events that ARE
// definitionally a stale chunk (Vite's vite:preloadError); no error-string check.
export function hardReloadForUpdate() {
  if (claimed()) return false
  claim()
  showUpdatingSplash()
  wipeAndReload()
  return true
}

async function wipeAndReload() {
  try { if ('caches' in window) { const keys = await caches.keys(); await Promise.all(keys.map(k => caches.delete(k))) } } catch { /* ignore */ }
  try { const reg = await navigator.serviceWorker?.getRegistration(); await reg?.unregister() } catch { /* ignore */ }
  // 2026-08-22 dead-nav fix: this path fired at app open (stale chunk after a
  // deploy) and did an in-place replace() on standalone — the exact navigation
  // the rest of the app suppresses because it desyncs the bottom nav's touch
  // targets until a force-quit. Caches + SW are cleared above, so on standalone
  // we ask for the force-quit the user was going to need anyway, up front.
  // Standalone used to stop here with a "close and reopen" message and NO navigation —
  // leaving a page whose SW and caches were just wiped and which can't load a single
  // lazy route. A broken app is worse than the fixed-nav drift a replace() risks
  // (main.jsx re-anchors fixed layers on pageshow anyway). Navigate (2026-09-13).
  if (isStandalonePWA) { showReopenMessage() }
  try {
    const u = new URL(window.location.href)
    u.searchParams.set('_v', Date.now().toString(36))   // cache-bust; preserves ?from=/?invite= etc.
    window.location.replace(u.toString())
  } catch { window.location.replace(window.location.href) }  // replace(), not reload(), iOS PWA fixed-position drift (see WhatsNew.handleUpdate)
}

// Recover only if `err` looks like a stale-chunk failure. Returns true if it took over.
export function recoverFromStaleChunk(err) {
  if (!isStaleChunkError(err)) return false
  return hardReloadForUpdate()
}
